import { apiFetch } from '../../../shared/api/apiFetch'

/**
 * One folder of a team's map-pin collection. `id` is what a pin's `folderId`
 * points at, which is how the two lists this feature fetches are joined.
 *
 * `name` can be empty: it is free text a hunter typed on their phone, and older
 * clients allowed a folder without one. Presentation decides what to show for an
 * unnamed folder; the wire shape reports it as it is stored.
 */
export interface TeamPinFolderDto {
  id: string
  name: string
}

interface ListTeamPinFoldersResponse {
  items: TeamPinFolderDto[]
}

// Trailing slash for the same reason as the map-pin collection root: the .NET
// list route is registered as `MapGet("/")` inside the
// `/clubhouse/teams/{teamId}/map-pin-folders` route group.
const base = (teamId: string) => `/clubhouse/teams/${encodeURIComponent(teamId)}/map-pin-folders`

export async function listTeamPinFolders(teamId: string, token: string): Promise<TeamPinFolderDto[]> {
  const r = await apiFetch<ListTeamPinFoldersResponse>(`${base(teamId)}/`, { token })
  return r.items
}

export async function createTeamPinFolder(
  teamId: string,
  name: string,
  token: string,
): Promise<TeamPinFolderDto> {
  return apiFetch<TeamPinFolderDto>(`${base(teamId)}/`, {
    method: 'POST',
    body: JSON.stringify({ name }),
    token,
  })
}

export async function renameTeamPinFolder(
  teamId: string,
  folderId: string,
  name: string,
  token: string,
): Promise<TeamPinFolderDto> {
  return apiFetch<TeamPinFolderDto>(`${base(teamId)}/${encodeURIComponent(folderId)}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
    token,
  })
}

/**
 * Deletes an EMPTY folder. The API refuses one that still holds pins or lines
 * (422) rather than deleting them with it — the same rule the app enforces, and
 * what keeps a pin from ever pointing at a folder nothing names. A caller has to
 * be ready to show that refusal.
 */
export async function deleteTeamPinFolder(
  teamId: string,
  folderId: string,
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`${base(teamId)}/${encodeURIComponent(folderId)}`, {
    method: 'DELETE',
    token,
  })
}
